// --- START EDIT: Hybrid layout Contact page with improved error styling and i18n ---
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2, Clock } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/components/ui/card";
import { useStoreSettings, type DaySchedule } from "@/lib/hooks/useStoreSettings";
import { Button } from "@/lib/components/ui/button";
import { Input } from "@/lib/components/ui/input";
import { Textarea } from "@/lib/components/ui/textarea";
import { Checkbox } from "@/lib/components/ui/checkbox";
import { Label } from "@/lib/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/ui/select";
// --- START EDIT: Use CSRF-aware axios client ---
import axiosInstance from "@/auth/axiosWithInterceptor";
// --- END EDIT: Use CSRF-aware axios client ---
import { useTranslation } from "react-i18next";
// --- END EDIT: Hybrid layout Contact page with improved error styling and i18n ---

// Constants
const COMPANY_EMAIL = "geral@dm-3dprinting.com";
const COMPANY_PHONE = "+351 937 427 191";
// --- START EDIT: Add second phone number constant ---
const COMPANY_PHONE_2 = "+351 917 370 007";
// --- END EDIT: Add second phone number constant ---
const COMPANY_ADDRESS = "Rua de Tomaz Ribeiro 733, 4450-298, Matosinhos, Portugal";
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/2aSfsJVzD7LkGTgL8";

// Form validation schema
const contactFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name must be 100 characters or less"),
  email: z.string().email("Please enter a valid email address"),
  topic: z.enum(["general", "order_support", "quote"], {
    required_error: "Please select a topic",
  }),
  order_code: z.string()
    .max(32, "Order code must be 32 characters or less")
    .regex(/^[A-Za-z0-9-]*$/, "Order code can only contain letters, numbers, and hyphens")
    .optional(),
  message: z.string()
    .min(10, "Message must be at least 10 characters")
    .max(4000, "Message must be 4000 characters or less"),
  send_copy: z.boolean(),
  website: z.string().optional(), // Honeypot
});

type ContactFormData = z.infer<typeof contactFormSchema>;

// Reusable error message component with improved contrast/spacing
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 text-sm font-medium text-red-600 dark:text-red-400" role="alert">
      {message}
    </p>
  );
}

// JSON-LD structured data component
function JsonLdScript() {
  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    // --- START EDIT: Include both phone numbers in structured data (best practice) ---
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "DM 3D Printing",
      url: origin,
      logo: `${origin}/logo.png`,
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: COMPANY_PHONE,
          email: COMPANY_EMAIL,
          contactType: "customer service",
          areaServed: "PT",
          availableLanguage: ["en", "pt"],
        },
        {
          "@type": "ContactPoint",
          telephone: COMPANY_PHONE_2,
          email: COMPANY_EMAIL,
          contactType: "customer service",
          areaServed: "PT",
          availableLanguage: ["en", "pt"],
        },
      ],
      address: {
        "@type": "PostalAddress",
        streetAddress: "Rua de Tomaz Ribeiro 733",
        addressLocality: "Matosinhos",
        postalCode: "4450-298",
        addressCountry: "PT",
      },
      sameAs: [
        "https://instagram.com/dm3dprinting",
      ],
    };
    // --- END EDIT: Include both phone numbers in structured data (best practice) ---

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(jsonLd);
    script.id = "contact-jsonld";
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("contact-jsonld");
      if (existing) {
        document.head.removeChild(existing);
      }
    };
  }, []);

  return null;
}

// ---------------------------------------------------------------------------
// Helpers for rendering working hours
// ---------------------------------------------------------------------------

function formatTimeSlots(schedule: DaySchedule): string | null {
  if (schedule.closed || schedule.slots.length === 0) return null;
  return schedule.slots.map((s) => `${s.open} – ${s.close}`).join(" | ");
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const Contact = () => {
  // --- START EDIT: Add translation hook ---
  const { t } = useTranslation('marketing');
  // --- END EDIT: Add translation hook ---
  const { settings: storeSettings, loading: hoursLoading } = useStoreSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      topic: undefined,
      order_code: "",
      message: "",
      send_copy: false,
      website: "", // Honeypot
    },
  });

  const watchTopic = watch("topic");
  const watchMessage = watch("message");
  const watchSendCopy = watch("send_copy");
  const isOrderSupport = watchTopic === "order_support";

  const onSubmit = async (data: ContactFormData) => {
    // Check honeypot
    if (data.website) {
      // Silently fail for bots
      setIsSubmitted(true);
      return;
    }

    // Validate order code for order support
    if (data.topic === "order_support" && !data.order_code?.trim()) {
      setError("order_code", {
        type: "manual",
        message: "Order code is required for order support inquiries",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const orderCode = data.order_code?.trim().toUpperCase();
      const payload = {
        name: data.name.trim(),
        email: data.email.trim(),
        topic: data.topic,
        order_code: orderCode && orderCode.length > 0 ? orderCode : null,
        message: data.message.trim(),
        send_copy: data.send_copy,
        website: data.website, // Include honeypot (should be empty)
      };

      // --- START EDIT: Use CSRF-aware axios client to ensure X-CSRF-Token header ---
      const response = await axiosInstance.post("/contact", payload, { withCredentials: true });
      // --- END EDIT: Use CSRF-aware axios client to ensure X-CSRF-Token header ---

      if (response.data.success) {
        setReferenceId(response.data.reference_id);
        setIsSubmitted(true);
        toast.success(t('contact.success.description'));
      }
    } catch (error: unknown) {
      // Axios error handling
      const err = error as { response?: { status?: number; data?: { detail?: string }; headers?: Record<string, string> } };
      if (err.response?.status === 429) {
        // Get retry-after from headers (axios uses lowercase keys)
        const retryAfter = err.response.headers?.["retry-after"] || "3600";
        toast.error(t('contact.errors.rateLimit', { minutes: Math.ceil(parseInt(retryAfter) / 60) }));
      } else if (err.response?.status === 400) {
        toast.error(err.response.data?.detail || t('contact.errors.sendFailed'));
      } else if (err.response?.status === 403) {
        // CSRF validation failed
        toast.error(t('contact.errors.sendFailed'));
      } else {
        toast.error(t('contact.errors.sendFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setReferenceId(null);
    reset();
  };

  // --- START EDIT: Helper for tel: href formatting ---
  const telHref = (num: string) => `tel:${num.replace(/\s/g, "")}`;
  // --- END EDIT: Helper for tel: href formatting ---

  return (
    <>
      <JsonLdScript />
      <div className="min-h-[calc(100vh-64px)] bg-muted text-foreground">
        {/* Hero - styled to match Material Guide */}
        <section className="px-6 pt-12 pb-8">
          <div className="max-w-6xl mx-auto text-center">
            {/* --- START EDIT: Use translations for hero --- */}
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              {t('contact.title')}
            </h1>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
            {/* --- END EDIT: Use translations for hero --- */}
          </div>
        </section>

        {/* Hybrid Layout: Cards left, Form right on desktop; stacked on mobile */}
        <section className="px-6 pb-12">
          {/* --- START EDIT: Stretch columns on desktop so the right card can match left stack height --- */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-start lg:items-stretch">
            {/* Contact Cards - left column on desktop */}
            <div className="lg:col-span-4 flex flex-col gap-4 lg:h-full">
              <Card className="hover:shadow-lg transition-shadow bg-card/90 backdrop-blur">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    {/* --- START EDIT: Use translation for email title --- */}
                    <CardTitle className="text-lg">{t('contact.info.email')}</CardTitle>
                    {/* --- END EDIT: Use translation for email title --- */}
                  </div>
                </CardHeader>
                <CardContent>
                  <a
                    href={`mailto:${COMPANY_EMAIL}`}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {COMPANY_EMAIL}
                  </a>
                  <CardDescription className="mt-2">
                    We respond within 24 hours
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow bg-card/90 backdrop-blur">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    {/* --- START EDIT: Use translation for phone title --- */}
                    <CardTitle className="text-lg">{t('contact.info.phone')}</CardTitle>
                    {/* --- END EDIT: Use translation for phone title --- */}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* --- START EDIT: Show both phone numbers --- */}
                  <div className="flex flex-col">
                    <a
                      href={telHref(COMPANY_PHONE)}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {COMPANY_PHONE}
                    </a>
                    <a
                      href={telHref(COMPANY_PHONE_2)}
                      className="mt-2 text-muted-foreground hover:text-primary transition-colors"
                    >
                      {COMPANY_PHONE_2}
                    </a>
                  </div>
                  {/* --- END EDIT: Show both phone numbers --- */}
                </CardContent>
              </Card>

              {/* Working Hours card — dynamic from store settings */}
              <Card className="hover:shadow-lg transition-shadow bg-card/90 backdrop-blur">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{t('contact.info.hours')}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {hoursLoading ? (
                    <div className="space-y-1.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-4 bg-muted rounded animate-pulse w-3/4" />
                      ))}
                    </div>
                  ) : storeSettings?.working_hours && storeSettings.working_hours.length > 0 ? (
                    <ul className="space-y-1 text-sm">
                      {storeSettings.working_hours.map((schedule) => {
                        const slots = formatTimeSlots(schedule);
                        return (
                          <li key={schedule.day} className="flex justify-between gap-4">
                            <span className="font-medium text-foreground w-24 shrink-0">
                              {t(`contact.days.${schedule.day}`, schedule.day)}
                            </span>
                            <span className={!slots ? "text-muted-foreground" : "text-foreground"}>
                              {slots ?? t('contact.closed')}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('contact.hoursNotAvailable')}</p>
                  )}
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow bg-card/90 backdrop-blur">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    {/* --- START EDIT: Use translation for location title --- */}
                    <CardTitle className="text-lg">{t('contact.info.address')}</CardTitle>
                    {/* --- END EDIT: Use translation for location title --- */}
                  </div>
                </CardHeader>
                <CardContent>
                  <a
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {COMPANY_ADDRESS}
                  </a>
                  <CardDescription className="mt-2">
                    <a
                      href={GOOGLE_MAPS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {/* --- START EDIT: Use translation for map link --- */}
                      {t('contact.info.viewMap')}
                      {/* --- END EDIT: Use translation for map link --- */}
                    </a>
                  </CardDescription>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form - right column on desktop */}
            <div className="lg:col-span-8 lg:h-full">
              {/* --- START EDIT: Force equal height only when form is shown; use flex to pin button & expand textarea --- */}
              <Card
                className={[
                  "bg-card/90 backdrop-blur",
                  !isSubmitted ? "lg:h-full lg:flex lg:flex-col" : "",
                ].join(" ")}
              >
                <CardHeader>
                  {/* --- START EDIT: Use translations for form header --- */}
                  <CardTitle>{t('contact.form.submit')}</CardTitle>
                  <CardDescription>
                    {t('contact.description')}
                  </CardDescription>
                  {/* --- END EDIT: Use translations for form header --- */}
                </CardHeader>

                <CardContent className={!isSubmitted ? "lg:flex-1 lg:flex lg:flex-col" : ""}>
                  {isSubmitted ? (
                    <div className="text-center py-8 space-y-4">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20">
                        <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                      </div>
                      {/* --- START EDIT: Use translations for success message --- */}
                      <h3 className="text-xl font-semibold">{t('contact.success.title')}</h3>
                      <p className="text-muted-foreground max-w-md mx-auto">
                        {t('contact.success.description')}
                      </p>
                      {/* --- END EDIT: Use translations for success message --- */}
                      {referenceId && (
                        <p className="text-sm text-muted-foreground">
                          Reference ID: <span className="font-mono">{referenceId}</span>
                        </p>
                      )}
                      <Button onClick={handleReset} variant="outline" className="mt-4">
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="space-y-6 lg:flex-1 lg:flex lg:flex-col lg:space-y-0"
                    >
                      {/* Honeypot - hidden from users */}
                      <input
                        type="text"
                        {...register("website")}
                        className="absolute -left-[9999px] opacity-0 pointer-events-none"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />

                      {/* --- START EDIT: Wrap form fields so button can pin to bottom on desktop --- */}
                      <div className="space-y-6 lg:flex-1 lg:flex lg:flex-col">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            {/* --- START EDIT: Use translations for form fields --- */}
                            <Label htmlFor="name">{t('contact.form.name')} *</Label>
                            <Input
                              id="name"
                              placeholder={t('contact.form.namePlaceholder')}
                              {...register("name")}
                              aria-invalid={errors.name ? "true" : "false"}
                            />
                            {/* --- END EDIT: Use translations for form fields --- */}
                            <FieldError message={errors.name?.message} />
                          </div>

                          <div className="space-y-2">
                            {/* --- START EDIT: Use translations for email field --- */}
                            <Label htmlFor="email">{t('contact.form.email')} *</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder={t('contact.form.emailPlaceholder')}
                              {...register("email")}
                              aria-invalid={errors.email ? "true" : "false"}
                            />
                            {/* --- END EDIT: Use translations for email field --- */}
                            <FieldError message={errors.email?.message} />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            {/* --- START EDIT: Use translations for topic field --- */}
                            <Label htmlFor="topic">{t('contact.form.topic')} *</Label>
                            <Select
                              onValueChange={(value) => setValue("topic", value as ContactFormData["topic"])}
                              value={watchTopic}
                            >
                              <SelectTrigger className="w-full" aria-invalid={errors.topic ? "true" : "false"}>
                                <SelectValue placeholder={t('contact.form.topicPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="general">{t('contact.form.topics.general')}</SelectItem>
                                <SelectItem value="order_support">{t('contact.form.topics.orderSupport')}</SelectItem>
                                <SelectItem value="quote">{t('contact.form.topics.quote')}</SelectItem>
                              </SelectContent>
                            </Select>
                            {/* --- END EDIT: Use translations for topic field --- */}
                            <FieldError message={errors.topic?.message} />
                          </div>

                          {isOrderSupport && (
                            <div className="space-y-2">
                              {/* --- START EDIT: Use translations for order code field --- */}
                              <Label htmlFor="order_code">{t('contact.form.orderCode')} *</Label>
                              <Input
                                id="order_code"
                                placeholder={t('contact.form.orderCodePlaceholder')}
                                {...register("order_code")}
                                onChange={(e) => setValue("order_code", e.target.value.toUpperCase())}
                                aria-invalid={errors.order_code ? "true" : "false"}
                              />
                              {/* --- END EDIT: Use translations for order code field --- */}
                              <p className="text-xs text-muted-foreground">
                                Enter your order code (found in your order confirmation email)
                              </p>
                              <FieldError message={errors.order_code?.message} />
                            </div>
                          )}
                        </div>

                        {/* --- START EDIT: Message area becomes flexible so textarea grows on desktop --- */}
                        <div className="space-y-2 lg:flex-1 lg:flex lg:flex-col">
                          {/* --- START EDIT: Use translations for message field --- */}
                          <Label htmlFor="message">{t('contact.form.message')} *</Label>
                          <Textarea
                            id="message"
                            placeholder={t('contact.form.messagePlaceholder')}
                            className="min-h-[150px] resize-y lg:flex-1 lg:min-h-0"
                            {...register("message")}
                            aria-invalid={errors.message ? "true" : "false"}
                          />
                          {/* --- END EDIT: Use translations for message field --- */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {watchMessage?.length || 0}/4000 characters
                            </span>
                          </div>
                          <FieldError message={errors.message?.message} />
                        </div>
                        {/* --- END EDIT: Message area becomes flexible so textarea grows on desktop --- */}

                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="send_copy"
                            checked={watchSendCopy}
                            onCheckedChange={(checked) => setValue("send_copy", checked === true)}
                          />
                          {/* --- START EDIT: Use translation for send copy label --- */}
                          <Label htmlFor="send_copy" className="font-normal cursor-pointer">
                            {t('contact.form.sendCopy')}
                          </Label>
                          {/* --- END EDIT: Use translation for send copy label --- */}
                        </div>
                      </div>
                      {/* --- END EDIT: Wrap form fields so button can pin to bottom on desktop --- */}

                      {/* --- START EDIT: Pin submit button to bottom on desktop --- */}
                      <div className="pt-6 lg:pt-6">
                        <Button type="submit" className="w-full" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {/* --- START EDIT: Use translation for submitting state --- */}
                              {t('contact.form.submitting')}
                              {/* --- END EDIT: Use translation for submitting state --- */}
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-4 w-4" />
                              {/* --- START EDIT: Use translation for submit button --- */}
                              {t('contact.form.submit')}
                              {/* --- END EDIT: Use translation for submit button --- */}
                            </>
                          )}
                        </Button>
                      </div>
                      {/* --- END EDIT: Pin submit button to bottom on desktop --- */}
                    </form>
                  )}
                </CardContent>
              </Card>
              {/* --- END EDIT: Force equal height only when form is shown; use flex to pin button & expand textarea --- */}
            </div>
          </div>
          {/* --- END EDIT: Stretch columns on desktop so the right card can match left stack height --- */}
        </section>

        {/* Response time note at bottom */}
        <section className="px-6 pb-16">
          <p className="text-center text-sm text-muted-foreground">
            {t('marketing:contact.responseTime')}
          </p>
        </section>
      </div>
    </>
  );
};

export default Contact;
// --- END EDIT ---